import * as React from "react";
import { clearLogs, listLogs, type AdminLogRow } from "@/api/admin";
import { useAction, useAsync } from "@/lib/useAsync";
import { when } from "@/lib/format";
import { useUrlParam } from "@/lib/useUrlParam";
import { FilterChips } from "@/components/FilterChips";
import { SearchBox, useUrlSearch } from "@/components/SearchBox";
import { Badge, type BadgeTone } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Notice } from "@/components/ui/feedback";
import { Select } from "@/components/ui/input";
import { ConfirmModal } from "@/components/ui/modal";
import { PageHeader } from "@/components/ui/page-header";
import { PagedList, usePagedCursor } from "@/components/ui/paged-list";
import { TD, TH, TR } from "@/components/ui/table";

const LEVEL_TONE: Record<string, BadgeTone> = { error: "red", warn: "amber", info: "blue" };

const LEVELS = [
  { value: "", label: "All" },
  { value: "error", label: "Errors" },
  { value: "warn", label: "Warnings" },
];

/**
 * What the worker wrote down when something went wrong.
 *
 * The log pages on its own AUTOINCREMENT id rather than on `(created_at, id)`
 * like the other lists, because rows arrive in bursts that share a second --
 * and a tie broken by anything other than insertion order shows the same error
 * twice.
 */
export function LogsScreen() {
  const [level, setLevel] = useUrlParam("level");
  const [source, setSource] = useUrlParam("source");
  const search = useUrlSearch();
  const q = search.value;

  // A clear empties the pages paged through, so it starts on page one too.
  const [clears, setClears] = React.useState(0);
  const filters = `${level}|${source}|${q}`;
  const pager = usePagedCursor(`${filters}|${clears}`);
  const { data, loading, error } = useAsync(
    () => listLogs({ level, source, q, cursor: pager.cursor }),
    [level, source, q, pager.cursor, clears],
  );

  const [open, setOpen] = React.useState<number | null>(null);
  const [confirming, setConfirming] = React.useState(false);
  // What the last clear did, said only until the view moves on.
  const [cleared, setCleared] = React.useState<{ filters: string; count: number } | null>(
    null,
  );
  const deleted = cleared?.filters === filters ? cleared.count : null;
  const action = useAction();
  const newestId = data?.newestId ?? null;

  async function clear() {
    if (newestId === null) return;
    setCleared(null);
    await action.run(async () => {
      setCleared({ filters, count: (await clearLogs(newestId)).deleted });
      setClears((n) => n + 1);
    });
    setConfirming(false);
  }

  const filtered = Boolean(level || source || q);

  return (
    <div className="space-y-4">
      <PageHeader title="Logs" description="Captured errors and warnings, newest first.">
        <Button
          size="sm"
          variant="outline"
          disabled={newestId === null || action.busy}
          onClick={() => setConfirming(true)}
        >
          Clear log
        </Button>
      </PageHeader>

      <div className="flex flex-wrap items-center gap-1.5">
        <FilterChips options={LEVELS} value={level} onChange={setLevel} />
        <Select
          size="sm"
          className="w-40"
          aria-label="Source"
          value={source}
          onChange={(e) => setSource(e.target.value)}
        >
          <option value="">Every source</option>
          {/* The one chosen stays offered after the log stops holding it. */}
          {[...new Set([...(data?.sources ?? []), ...(source ? [source] : [])])].map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Select>
        <SearchBox search={search} label="Search the log" placeholder="Message, job, fields" />
      </div>

      {action.error && <Notice tone="error">{action.error}</Notice>}
      {deleted !== null && <Notice tone="success">{`Cleared ${deleted} log lines.`}</Notice>}

      <PagedList
        columns={6}
        rows={data?.rows ?? []}
        loading={loading}
        error={error}
        empty={filtered ? "No line matches that filter." : "Nothing has been logged."}
        pager={pager}
        nextCursor={data?.nextCursor}
        head={
          <>
            <TH>When</TH>
            <TH>Level</TH>
            <TH>Where</TH>
            <TH>Message</TH>
            <TH>Fields</TH>
            <TH />
          </>
        }
        renderRow={(row) => (
          <LogLine
            key={row.id}
            row={row}
            open={open === row.id}
            onToggle={() => setOpen(open === row.id ? null : row.id)}
          />
        )}
      />

      <ConfirmModal
        open={confirming}
        title="Clear the log?"
        confirmLabel="Clear"
        busy={action.busy}
        onClose={() => setConfirming(false)}
        onConfirm={clear}
        body={
          <p>
            Every line up to the newest one here is deleted, whatever the filters show. A line
            written after this screen loaded is kept.
          </p>
        }
      />
    </div>
  );
}

function LogLine({
  row,
  open,
  onToggle,
}: {
  row: AdminLogRow;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <TR>
        <TD className="whitespace-nowrap text-muted-foreground">{when(row.created_at)}</TD>
        <TD>
          <Badge color={LEVEL_TONE[row.level] ?? "gray"}>{row.level}</Badge>
        </TD>
        <TD className="text-secondary">
          {row.source ?? "—"}
          {row.job ? <span className="text-muted-foreground"> · {row.job}</span> : null}
        </TD>
        <TD className="max-w-[360px]">
          <div className="break-words">{row.message}</div>
          {row.error_name && <div className="text-xs text-muted-foreground">{row.error_name}</div>}
        </TD>
        <TD className="max-w-[260px]">
          {row.fields ? (
            <pre
              className={
                "line-clamp-3 whitespace-pre-wrap break-all font-mono text-[11px] " +
                "leading-snug text-muted-foreground"
              }
            >
              {row.fields}
            </pre>
          ) : (
            "—"
          )}
        </TD>
        <TD>
          <Button size="sm" variant="ghost" aria-expanded={open} onClick={onToggle}>
            {open ? "Hide" : "Details"}
          </Button>
        </TD>
      </TR>
      {open && (
        <TR>
          <TD colSpan={6} className="bg-muted/40">
            <Detail label="Fields" text={row.fields} />
            <Detail label="Stack" text={row.stack} />
          </TD>
        </TR>
      )}
    </>
  );
}

function Detail({ label, text }: { label: string; text: string | null }) {
  return (
    <div className="space-y-1 py-1">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <pre
        className={
          "max-h-64 overflow-auto whitespace-pre-wrap break-all font-mono text-[11px] leading-snug"
        }
      >
        {text ?? "—"}
      </pre>
    </div>
  );
}
